import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { tests } from 'us-react-components/dist/constants/Tests';
import {
  BackgroundCheckUrlService,
  SitterProfileStatus,
  SitterProfileStatusService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const POLL_MS = 2500;
const MAX_TRIES = 12;

export const FinishBackgroundCheck = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter;

  const [url, setUrl] = useState<string | null>(null);
  const [detailKey, setDetailKey] = useState<string>('pages.finishBackgroundCheck.preparing');
  const [stripeMessage, setStripeMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const triesRef = useRef(0);
  const cancelledRef = useRef(false);

  const redirectStatus = searchParams.get('redirect_status');
  const purchased = searchParams.get('purchased');
  const isNux = !!searchParams.get('nux');

  useEffect(() => {
    document.title = 'UrbanSitter - Background Check';
  }, []);

  const goToBuy = useCallback((): void => {
    const isTester = stores.user.inTestGroup(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT);
    if (isTester) {
      window.location.href =
        redirectStatus === 'failed'
          ? '/app/caregiver/buy-background-check?purchased=error'
          : '/app/caregiver/buy-background-check?purchased=false';
    } else {
      navigate('/buy-background-check?purchased=false', { replace: true });
    }
  }, [navigate, user, redirectStatus]);

  const checkStatus = useCallback(async (): Promise<void> => {
    if (cancelledRef.current) return;
    if (triesRef.current >= MAX_TRIES) {
      setTimedOut(true);
      setDetailKey('pages.finishBackgroundCheck.stillPreparing');
      setIsLoading(false);
      return;
    }
    triesRef.current += 1;

    if (!user?.id || !sitter?.id) return;

    try {
      const statusResp = (await SitterProfileStatusService.get(user.id)).data;
      const status = (statusResp?.data ?? null) as SitterProfileStatus | null;
      if (cancelledRef.current) return;
      const cleared = status?.backgroundCheckCleared;
      if (cleared?.url) {
        setUrl(cleared.url);
        setDetailKey('pages.finishBackgroundCheck.ready');
        setIsLoading(false);
      } else if (triesRef.current === 3) {
        // After a few tries, prod the backend to generate a link if the
        // sitter has completed purchase but the URL isn't cached yet.
        try {
          const linkResp = (await BackgroundCheckUrlService.create({ sitterId: sitter.id })).data;
          const link = (linkResp?.data ?? null) as { url?: string } | null;
          if (cancelledRef.current) return;
          if (link?.url) {
            setUrl(link.url);
            setDetailKey('pages.finishBackgroundCheck.ready');
            setIsLoading(false);
            return;
          }
        } catch {
          // fall through to poll again
        }
        setTimeout(checkStatus, POLL_MS);
      } else {
        setTimeout(checkStatus, POLL_MS);
      }
    } catch {
      setIsLoading(false);
    }
  }, [user?.id, sitter?.id]);

  const checkExistingPurchase = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const resp = (await SitterProfileStatusService.get(user.id)).data;
      const status = (resp?.data ?? null) as SitterProfileStatus | null;
      if (cancelledRef.current) return;
      if (!status?.backgroundCheckPurchased?.complete) {
        goToBuy();
      } else {
        checkStatus();
      }
    } catch {
      setIsLoading(false);
    }
  }, [user?.id, checkStatus, goToBuy]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!user?.id) return;

    if (redirectStatus === 'failed') {
      // Clear stripe-redirect URL params, mark as error, route to buy page.
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.delete('payment_intent');
        next.delete('payment_intent_client_secret');
        next.set('purchased', 'error');
        return next;
      }, { replace: true });
      goToBuy();
      return;
    }

    if (redirectStatus === 'succeeded' && searchParams.get('payment_intent')) {
      setStripeMessage(t('pages.finishBackgroundCheck.paymentProcessed'));
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.delete('payment_intent');
        next.delete('payment_intent_client_secret');
        next.delete('redirect_status');
        next.set('purchased', 'true');
        return next;
      }, { replace: true });
      checkStatus();
      return;
    }

    if (purchased === null || purchased === undefined) {
      checkExistingPurchase();
    } else if (purchased === 'false' || purchased === 'error') {
      goToBuy();
    } else {
      checkStatus();
    }

    return (): void => {
      cancelledRef.current = true;
    };
    // Reads from URL snapshot; we don't want to re-fire on each searchParams change.

  }, [user?.id]);

  const [showStripeBanner, setShowStripeBanner] = useState(true);

  if (isLoading) {
    return (
      <div className='container py-8 max-w-3xl mx-auto'>
        <p className='text-center'>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className='container py-8 max-w-5xl mx-auto'>
      {stripeMessage && showStripeBanner && (
        <div className='us-card p-6 mb-5 rounded bg-emerald-50 border-emerald-200'>
          <div className='flex items-start justify-between'>
            <h3 className='text-lg font-semibold text-emerald-800 mb-2'>
              {t('pages.finishBackgroundCheck.stripeHeading')}
            </h3>
            <Button
              buttonType={ButtonType.tertiary}
              aria-label='Close'
              onClick={(): void => setShowStripeBanner(false)}
              className='text-2xl leading-none'
            >
              ×
            </Button>
          </div>
          <p className='text-emerald-800 mb-1'>{stripeMessage}</p>
          <p className='text-sm text-emerald-800'>
            {t('pages.finishBackgroundCheck.stripeSeparateTab')}
          </p>
        </div>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div>
          <h1 className='text-2xl font-semibold mb-3'>
            {t('pages.finishBackgroundCheck.mainHeading')}
          </h1>
          <hr className='border-emerald-500 border-t-2 mb-6' />
        </div>
        <div className='lg:col-span-2'>
          <div className='us-card p-6 rounded'>
            <h3 className='text-lg font-semibold mb-3'>
              {t('pages.finishBackgroundCheck.nextStep')}
            </h3>
            <p className='mb-8'>{t(detailKey)}</p>

            {!timedOut && (
              <Button
                id='finish-button'
                buttonType={ButtonType.primary}
                disabled={!url}
                onClick={(): void => {
                  if (!url) return;
                  if (isNux) {
                    setSearchParams((sp) => {
                      const next = new URLSearchParams(sp);
                      next.delete('purchased');
                      next.delete('nux');
                      return next;
                    }, { replace: true });
                  }
                  window.open(url);
                }}
              >
                {!url && (
                  <span
                    className='mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin align-middle'
                    aria-hidden='true'
                  />
                )}
                {url
                  ? t('pages.finishBackgroundCheck.finishLabel')
                  : t('pages.finishBackgroundCheck.preparingLabel')}
              </Button>
            )}

            {timedOut && (
              <Button
                id='dashboard-button'
                buttonType={ButtonType.primary}
                onClick={(): void => {
                  window.location.href = '/app/caregiver/dashboard';
                }}
              >
                {t('pages.finishBackgroundCheck.goToDashboard')}
              </Button>
            )}

            {isNux && !timedOut && (
              <a
                id='skip-nux-button'
                href='/app/job-board?nux=true'
                className='inline-block align-middle ml-10 underline'
              >
                {t('pages.finishBackgroundCheck.skipForNow')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
